import { useEffect, useRef, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getMyPblLawyerCvRevision, saveMyPblLawyerCvDraft, submitMyPblLawyerCvRevision } from '@/api/pbl.api';
import { FormTitle, InputWrapper, MultiInputWrapper } from '@/components/Container/AuthContainer/styles';
import PageContainer from '@/components/Container/PageContainer';
import { ToggleGroup } from '@/components/Pbl/VolunteerRegistration/shared';
import { Button } from '@/components/UI/Button';
import { pblLanguages, pblLawFirmPositions, pblProfessionalQualifications, pblServiceAreas } from '@/data/pbl';
import useResize from '@/hooks/useResize';
import { FormBox, LoadingPage } from '@/layout';
import type { PblCvAuthorisation, PblLawyerCvRevisionStatus, PblLawyerProfile, PblStorageFile } from '@/types/pbl';
import AuthorisationFields from './AuthorisationFields';
import FirmAuthorisationUpload from './FirmAuthorisationUpload';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { ActionGroup, FormLayout, FormSection, LockedNotice, ProfileDescription, StatusPanel } from './styles';
import {
  buildLawyerCvPayload,
  defaultLawyerCvFormValues,
  mergeLawyerCvWorkspacePayload,
  normalizeLawyerCvFormValues,
  type LawyerCvFormValues,
  type LawyerCvTextFieldName,
} from './formValues';

type LawyerCvField = {
  name: LawyerCvTextFieldName;
  labelKey: string;
  multiline?: boolean;
};

const basicInformationRows: LawyerCvField[][] = [
  [
    { name: 'full_name_en', labelKey: 'fullNameEn' },
    { name: 'full_name_zh', labelKey: 'fullNameZh' },
  ],
  [
    { name: 'law_firm_name', labelKey: 'lawFirmEn' },
    { name: 'law_firm_name_zh', labelKey: 'lawFirmZh' },
  ],
];

const cvInformationRows: LawyerCvField[][] = [
  [
    { name: 'cv_full_name_en', labelKey: 'cvNameEn' },
    { name: 'cv_full_name_zh', labelKey: 'cvNameZh' },
  ],
  [
    { name: 'cv_law_firm_en', labelKey: 'cvFirmEn' },
    { name: 'cv_law_firm_zh', labelKey: 'cvFirmZh' },
  ],
  [
    { name: 'cv_professional_experience_en', labelKey: 'professionalExperienceEn', multiline: true },
    { name: 'cv_professional_experience_zh', labelKey: 'professionalExperienceZh', multiline: true },
  ],
  [
    { name: 'cv_pro_bono_experience_en', labelKey: 'proBonoExperienceEn', multiline: true },
    { name: 'cv_pro_bono_experience_zh', labelKey: 'proBonoExperienceZh', multiline: true },
  ],
];

const PblLawyerCv = () => {
  // @ts-ignore -- the legacy translation catalog exceeds TypeScript's recursive key inference limit
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>) => t(key as any, options as any) as string;
  const { isMobile, isTablet } = useResize();
  const [status, setStatus] = useState<PblLawyerCvRevisionStatus | ''>('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [reviewReason, setReviewReason] = useState('');
  const [profileId, setProfileId] = useState<number>();
  const [revisionId, setRevisionId] = useState<number>();
  const [savedAuthorisation, setSavedAuthorisation] = useState<PblCvAuthorisation>();
  const [firmAuthorisationFile, setFirmAuthorisationFile] = useState<PblStorageFile | null>(null);
  const baselinePayloadRef = useRef<Partial<PblLawyerProfile>>({});
  const locked = status === 'pending_review';
  const { control, handleSubmit, reset, watch, setValue } = useForm<LawyerCvFormValues>({
    defaultValues: defaultLawyerCvFormValues,
  });
  const selectedPosition = watch('position_in_law_firm');
  const selectedQualifications = watch('other_professional_qualifications');
  const selectedLanguages = watch('language_proficiency');
  const preview = buildLawyerCvPayload(watch(), baselinePayloadRef.current);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getMyPblLawyerCvRevision();
        if (!mounted) return;

        const workspace = response.data.data;
        const source = mergeLawyerCvWorkspacePayload(
          workspace.published_payload || {},
          workspace.editable?.payload || {}
        );
        const editableAuthorisation = workspace.editable?.status === 'rejected'
          ? undefined : workspace.editable?.authorisation;
        const authorisation = editableAuthorisation ?? workspace.published_authorisation;
        Object.assign(source, authorisation);
        baselinePayloadRef.current = source;
        setSavedAuthorisation(authorisation);
        setFirmAuthorisationFile(workspace.firm_authorisation_file || null);
        setRevisionId(editableAuthorisation ? workspace.editable?.id : undefined);
        setProfileId(workspace.lawyer_profile_id);
        setReviewRequired(!!workspace.cv_review_required);
        setReviewReason(workspace.editable?.review_reason || '');
        reset(normalizeLawyerCvFormValues(source));
        setStatus(workspace.editable?.status || workspace.published?.status || 'approved');
      } catch (error: any) {
        if (mounted) {
          errorHandler(error.response?.data?.message || t('pbl.backlogEnhancements.cv.loadFailed'));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [reset, t]);

  const saveDraft = handleSubmit(async (values) => {
    setBusy(true);
    try {
      const response = await saveMyPblLawyerCvDraft(buildLawyerCvPayload(values, baselinePayloadRef.current));
      setStatus(response.data.data.status);
      setRevisionId(response.data.data.id);
      setSavedAuthorisation(response.data.data.authorisation ?? undefined);
      successHandler(t('pbl.backlogEnhancements.cv.draftSaved'));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.backlogEnhancements.cv.saveFailed')
      );
    } finally {
      setBusy(false);
    }
  });

  const submitForReview = handleSubmit(async (values) => {
    if (
      !values.cv_publication_consent ||
      !values.firm_authorisation_file_id ||
      !values.firm_authorisation_confirmed ||
      !values.firm_authorisation_letter_attached ||
      !values.law_firm_change_notify_agreed
    ) {
      errorHandler(tr('pbl.cvRevisionUpdate.confirmationsRequired'));
      return;
    }
    setBusy(true);
    try {
      await saveMyPblLawyerCvDraft(buildLawyerCvPayload(values, baselinePayloadRef.current));
      const response = await submitMyPblLawyerCvRevision();
      setStatus(response.data.data.status);
      setRevisionId(response.data.data.id);
      setSavedAuthorisation(response.data.data.authorisation ?? undefined);
      setReviewRequired(true);
      setReviewReason('');
      successHandler(t('pbl.backlogEnhancements.cv.submitted'));
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || error.response?.data?.data || t('pbl.backlogEnhancements.cv.submitFailed')
      );
    } finally {
      setBusy(false);
    }
  });

  const renderTextField = ({ name, labelKey, multiline = false }: LawyerCvField) => (
    <Controller
      key={name}
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          label={tr(`pbl.backlogEnhancements.cv.fields.${labelKey}`)}
          variant="standard"
          value={field.value ?? ''}
          onChange={(event) => {
            field.onChange(event);
            const pairedCvField = {
              full_name_en: 'cv_full_name_en',
              full_name_zh: 'cv_full_name_zh',
              law_firm_name: 'cv_law_firm_en',
              law_firm_name_zh: 'cv_law_firm_zh',
            }[name as 'full_name_en' | 'full_name_zh' | 'law_firm_name' | 'law_firm_name_zh'];
            if (pairedCvField)
              setValue(pairedCvField as LawyerCvTextFieldName, event.target.value, { shouldDirty: true });
            if (name === 'law_firm_name' || name === 'law_firm_name_zh') {
              setValue('firm_authorisation_confirmed', false, { shouldDirty: true });
            }
          }}
          sx={{ width: '100%' }}
          multiline={multiline}
          rows={multiline ? 3 : undefined}
          error={!!error}
          helperText={error?.message}
          autoComplete="off"
          InputProps={{ readOnly: locked }}
        />
      )}
    />
  );

  const renderRows = (rows: LawyerCvField[][]) =>
    rows.map((row) => (
      <MultiInputWrapper key={row.map(({ name }) => name).join('-')} isMobile={isMobile}>
        {row.map(renderTextField)}
      </MultiInputWrapper>
    ));

  return (
    <PageContainer title={tr('pbl.lawyerProfile.title')}>
      <ProfileDescription>{tr('pbl.lawyerProfile.description')}</ProfileDescription>
      <LoadingPage isLoading={loading || busy} />
      <FormLayout>
        <FormBox onSubmit={submitForReview} isTablet={isTablet}>
          <StatusPanel role="status">
            <span>{t('pbl.backlogEnhancements.cv.revisionStatus')}</span>
            <strong>
              {status
                ? tr(`pbl.backlogEnhancements.cv.status.${status === 'resubmit_required' ? 'returned' : status}`, {
                    defaultValue: status,
                  })
                : t('pbl.backlogEnhancements.common.loading')}
            </strong>
          </StatusPanel>

          {locked ? <LockedNotice>{t('pbl.backlogEnhancements.cv.locked')}</LockedNotice> : null}
          <LockedNotice>
            {tr(reviewRequired ? 'pbl.cvRevisionUpdate.hiddenUntilApproved' : 'pbl.cvRevisionUpdate.submitNotice')}
          </LockedNotice>
          {reviewReason ? (
            <LockedNotice>
              {tr('pbl.cvRevisionUpdate.reviewReason')}: {reviewReason}
            </LockedNotice>
          ) : null}

          <FormSection>
            <FormTitle>{t('personalInfo')}</FormTitle>
            {renderRows(basicInformationRows)}
            <MultiInputWrapper isMobile={isMobile}>
              <InputWrapper>
                <Controller
                  control={control}
                  name="hk_solicitor_admission_year"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={t('pbl.backlogEnhancements.cv.fields.admissionYear')}
                      variant="standard"
                      type="number"
                      value={field.value}
                      sx={{ width: '100%' }}
                      onChange={(event) => field.onChange(event.target.value === '' ? '' : Number(event.target.value))}
                      error={!!error}
                      helperText={error?.message}
                      autoComplete="off"
                      InputProps={{ readOnly: locked }}
                    />
                  )}
                />
              </InputWrapper>
              <InputWrapper marginBottom={0}>
                <Controller
                  control={control}
                  name="position_in_law_firm"
                  render={({ field, fieldState: { error } }) => (
                    <FormControl error={!!error} fullWidth>
                      <FormTitle>{t('pbl.backlogEnhancements.cv.fields.position')}</FormTitle>
                      <RadioGroup {...field} row value={field.value || ''}>
                        {pblLawFirmPositions.map((position) => (
                          <FormControlLabel
                            key={position.value}
                            value={position.value}
                            control={<Radio color="primary" disabled={locked} />}
                            label={tr(position.labelKey, { defaultValue: position.defaultLabel })}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}
                />
                {selectedPosition === 'other' ? (
                  <Controller
                    control={control}
                    name="position_other_text"
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label={tr('pbl.lawyerRegistration.practiceInformation.fields.otherPosition', {
                          defaultValue: 'Other Position',
                        })}
                        variant="standard"
                        value={field.value || ''}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                        autoComplete="off"
                        InputProps={{ readOnly: locked }}
                      />
                    )}
                  />
                ) : null}
              </InputWrapper>
            </MultiInputWrapper>
          </FormSection>

          <FormSection>
            <FormTitle>{t('additionalInformation')}</FormTitle>
            {renderRows(cvInformationRows)}
            <InputWrapper>
              <FormTitle>
                {tr('pbl.lawyerRegistration.personalInformation.fields.otherProfessionalQualifications', {
                  defaultValue: 'Other Professional Qualifications',
                })}
              </FormTitle>
              <Controller
                control={control}
                name="other_professional_qualifications"
                render={({ field }) => (
                  <ToggleGroup
                    name={field.name}
                    options={pblProfessionalQualifications.map((qualification) => ({
                      value: qualification.value,
                      label: tr(qualification.labelKey, { defaultValue: qualification.defaultLabel }),
                    }))}
                    values={field.value || []}
                    onChange={field.onChange}
                    disabled={locked}
                  />
                )}
              />
              {selectedQualifications.includes('other') ? (
                <Controller
                  control={control}
                  name="other_professional_qualification_text"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={tr('pbl.lawyerRegistration.personalInformation.fields.pleaseSpecify', {
                        defaultValue: 'Please specify',
                      })}
                      variant="standard"
                      value={field.value || ''}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      autoComplete="off"
                      InputProps={{ readOnly: locked }}
                    />
                  )}
                />
              ) : null}
            </InputWrapper>
            <InputWrapper>
              <FormTitle>{t('pbl.backlogEnhancements.cv.fields.serviceAreas')}</FormTitle>
              <Controller
                control={control}
                name="service_areas"
                render={({ field }) => (
                  <ToggleGroup
                    name={field.name}
                    options={pblServiceAreas.map((area) => ({
                      value: area.value,
                      label: tr(area.labelKey, { defaultValue: area.defaultLabel }),
                    }))}
                    values={field.value || []}
                    onChange={field.onChange}
                    disabled={locked}
                  />
                )}
              />
            </InputWrapper>
            <InputWrapper>
              <FormTitle>{t('pbl.backlogEnhancements.cv.fields.languageProficiency')}</FormTitle>
              <Controller
                control={control}
                name="language_proficiency"
                render={({ field }) => (
                  <ToggleGroup
                    name={field.name}
                    options={pblLanguages.map((language) => ({
                      value: language.value,
                      label: tr(language.labelKey, { defaultValue: language.defaultLabel }),
                    }))}
                    values={field.value || []}
                    onChange={field.onChange}
                    disabled={locked}
                  />
                )}
              />
              {selectedLanguages.includes('other') ? (
                <Controller
                  control={control}
                  name="language_other_text"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label={tr('pbl.lawyerRegistration.personalInformation.fields.otherLanguage', {
                        defaultValue: 'Other language',
                      })}
                      variant="standard"
                      value={field.value || ''}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      autoComplete="off"
                      InputProps={{ readOnly: locked }}
                    />
                  )}
                />
              ) : null}
            </InputWrapper>
          </FormSection>

          <FormSection>
            <FormTitle>{tr('pbl.lawyerRegistration.steps.profileCv')}</FormTitle>
            {(
              [
                'cv_position',
                'cv_admission_year',
                'cv_other_qualifications',
                'cv_languages',
                'cv_primary_practice_areas',
              ] as const
            ).map((field) => (
              <MultiInputWrapper key={field} isMobile={isMobile}>
                {(['en', 'zh'] as const).map((language) => (
                  <TextField
                    key={language}
                    label={`${tr(`pbl.cvRevisionUpdate.${field}`)} (${language === 'en' ? 'English' : '中文'})`}
                    value={preview[`${field}_${language}`] || ''}
                    variant="standard"
                    fullWidth
                    multiline
                    InputProps={{ readOnly: true }}
                  />
                ))}
              </MultiInputWrapper>
            ))}
            <Controller
              control={control}
              name="cv_publication_consent"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={locked}
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label={tr('pbl.lawyerRegistration.profileCv.publicationConsent')}
                />
              )}
            />
          </FormSection>
          <FormSection>
            <FormTitle>{tr('pbl.lawyerRegistration.steps.authorisation')}</FormTitle>
            <AuthorisationFields
              control={control}
              disabled={locked || busy || loading}
              profileId={profileId}
              revisionId={revisionId}
              saved={savedAuthorisation}
            />
          </FormSection>

          <FirmAuthorisationUpload
            currentFile={firmAuthorisationFile}
            disabled={loading || busy || uploading}
            onUploading={setUploading}
            onUploaded={(file) => {
              setFirmAuthorisationFile(file);
              // The replacement endpoint updates the current profile, while rejected revisions stay immutable.
              setRevisionId(undefined);
              setSavedAuthorisation((current) => ({
                ...current,
                firm_authorisation_file_id: file.id,
                file_name: file.name,
                firm_authorisation_letter_attached: true,
              }));
              setValue('firm_authorisation_file_id', file.id);
              setValue('firm_authorisation_letter_attached', true);
            }}
          />

          <ActionGroup isMobile={isMobile}>
            <Button
              type="button"
              size="md"
              variant="primary-invert"
              rounded={false}
              disabled={busy || loading || locked || uploading}
              onClick={saveDraft}
            >
              {t('pbl.backlogEnhancements.cv.saveDraft')}
            </Button>
            <Button
              type="submit"
              size="md"
              variant="primary"
              rounded={false}
              disabled={busy || loading || locked || uploading}
            >
              {t('pbl.backlogEnhancements.cv.submitForReview')}
            </Button>
          </ActionGroup>
        </FormBox>
      </FormLayout>
    </PageContainer>
  );
};

export default PblLawyerCv;
